<template>
  <div>
    <v-btn-toggle
      v-if="dataType === dataValues[dataTypes.expression]"
      v-model="tissueData"
      class="mb-3"
      light
    >
      <v-btn flat :value="tissueTypes.tumor" color="primary">
        Tumor
      </v-btn>
      <v-btn flat :value="tissueTypes.normal" color="primary">
        Normal
      </v-btn>
    </v-btn-toggle>
    <BaseDataTables
      :options="options"
      :events="['init', 'stateSaveParams', 'search', 'xhr']"
      @init="initHandler"
      @stateSaveParams="stateSaveParamsHandler"
      @xhr="xhrHandler"
      @search="searchHandler"
      class="table-striped"
      width="100%"
      ref="table"
    >
      <thead>
      <tr>
        <th
          v-for="header in headers"
          :key="header.metaId"
          :data-meta-id="header.metaId"
          :data-tumor-type="header.tumorType"
          style="position: relative"
        >
          <div class="heatmap-header-flag" :style="{ backgroundColor: header.color }"></div>
          {{header.metaName}}
        </th>
      </tr>
      </thead>
    </BaseDataTables>
  </div>
</template>

<script>
// import 'datatables.net-fixedcolumns-bs4';
// import 'datatables.net-fixedcolumns-bs4/css/fixedColumns.bootstrap4.min.css';
// import 'datatables.net-fixedheader-bs4';
// import 'datatables.net-fixedheader-bs4/css/fixedHeader.bootstrap4.min.css';
// import 'datatables.net-scroller-bs4';
// import 'datatables.net-scroller-bs4/css/scroller.bootstrap4.min.css';
import { integratePlugin, enablePipeline, getMdColors } from '@/assets/js/utils';
import getMetaTableApi from '@/api/explore';
import { HEADERS_MAP, META_INFO, SEARCH_ID_RNA_VALUES } from '@/assets/data/MetaTable';
import { RNA_TYPES, RNA_VALUES } from '@/assets/data/RNATypes';
import { DATA_TYPES, DATA_VALUES } from '@/assets/data/DataTypes';
import HeatmapSelector from './HeatmapHeaderSelector';
import HeatmapLegend from './HeatmapLegend';

// assign colors to meta info
(() => {
  const colors = getMdColors(0);
  // color marker
  const colorMap = Object.create(null);
  let idx = 0;
  Object.keys(META_INFO).forEach((meta) => {
    const metaEntity = META_INFO[meta];
    const { tumorType } = metaEntity;
    if (!tumorType) {
      return;
    }
    if (!(tumorType in colorMap)) {
      colorMap[tumorType] = colors[idx];
      idx += 1;
    }
    metaEntity.color = colorMap[tumorType];
  });
})();

// assign meta info to each header
Object.keys(HEADERS_MAP).forEach((rnaType) => {
  const innerObj = HEADERS_MAP[rnaType];
  Object.keys(innerObj).forEach((dataType) => {
    innerObj[dataType] = innerObj[dataType].map((colName) => {
      const { name: metaName, tumorType, color } = META_INFO[colName];
      // non tumor header, colName doesn't represent metaId
      if (!tumorType || !color) {
        return { metaName };
      }
      // tumor header
      return {
        metaId: colName,
        metaName,
        tumorType,
        color,
      };
    });
  });
});

const TISSUE_TYPES = {
  tumor: 'tumor',
  normal: 'normal',
};

export default {
  props: {
    dataType: {
      type: String,
      required: true,
    },
    rnaType: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      tissueTypes: {
        ...TISSUE_TYPES,
      },
      dataTypes: {
        ...DATA_TYPES,
      },
      dataValues: {
        ...DATA_VALUES,
      },
      tissueData: TISSUE_TYPES.tumor,
    };
  },
  computed: {
    headers() {
      const allHeaders = HEADERS_MAP[this.rnaType][this.dataType];

      if (this.dataType === DATA_VALUES[DATA_TYPES.diffExpression]) {
        return allHeaders;
      }

      if (this.tissueData === TISSUE_TYPES.normal) {
        return allHeaders.filter(header => !header.tumorType || header.tumorType === 'Normal');
      }
      return allHeaders.filter(header => !header.tumorType || header.tumorType !== 'Normal');
    },
    tableId() {
      return `${this.rnaType}-${this.dataType}`;
    },
    url() {
      return getMetaTableApi(this.tableId);
    },
    selectorData() {
      const results = Object.create(null);
      this.headers.forEach((meta) => {
        const { tumorType, color } = meta;
        if (!tumorType || !color) {
          return;
        }
        if (!(tumorType in results)) {
          results[tumorType] = {
            color,
            options: [],
          };
        }
        results[tumorType].options.push(meta);
      });
      return results;
    },
    searchableCol() {
      // circRNA will search gene symbol (2nd column) instead of gene id (1st column)
      return this.rnaType === RNA_VALUES[RNA_TYPES.circRNA] ? 1 : 0;
    },
    searchLabel() {
      const keyword = SEARCH_ID_RNA_VALUES.includes(this.rnaType) ? 'gene id' : 'gene symbol';
      return `Search by ${keyword}`;
    },
    selectorOptions() {
      return {
        data: this.selectorData,
      };
    },
    legendOptionsScores() {
      const MAP = {
        [RNA_VALUES[RNA_TYPES.miRNA]]: {
          [DATA_VALUES[DATA_TYPES.expression]]: {
            max: 3,
            min: -2.5,
          },
          [DATA_VALUES[DATA_TYPES.diffExpression]]: {
            max: 2.5,
            min: -2.5,
          },
        },
        [RNA_VALUES[RNA_TYPES.circRNA]]: {
          [DATA_VALUES[DATA_TYPES.expression]]: {
            max: 3,
            min: -2.5,
          },
          [DATA_VALUES[DATA_TYPES.diffExpression]]: {
            max: 3,
            min: -2.5,
          },
        },
        [RNA_VALUES[RNA_TYPES.codingRNA]]: {
          [DATA_VALUES[DATA_TYPES.expression]]: {
            max: 3,
            min: -2.5,
          },
          [DATA_VALUES[DATA_TYPES.diffExpression]]: {
            max: 3,
            min: -2.7,
          },
        },
        [RNA_VALUES[RNA_TYPES.lncRNA]]: {
          [DATA_VALUES[DATA_TYPES.expression]]: {
            max: 3,
            min: -2.6,
          },
          [DATA_VALUES[DATA_TYPES.diffExpression]]: {
            max: 3,
            min: -2.5,
          },
        },
        [RNA_VALUES[RNA_TYPES.piRNA]]: {
          [DATA_VALUES[DATA_TYPES.expression]]: {
            max: 3,
            min: -2.5,
          },
          [DATA_VALUES[DATA_TYPES.diffExpression]]: {
            max: 3,
            min: -2.5,
          },
        },
        [RNA_VALUES[RNA_TYPES.tRFRNA]]: {
          [DATA_VALUES[DATA_TYPES.expression]]: {
            max: 3,
            min: -2.5,
          },
          [DATA_VALUES[DATA_TYPES.diffExpression]]: {
            max: 3,
            min: -2.5,
          },
        },
      };
      return MAP[this.rnaType][this.dataType];
    },
    legendOptions() {
      return {
        maxText: 'High Expression',
        minText: 'Low Expression',
        ...this.legendOptionsScores,
        colorRange: [
          // min
          this.getColorRange(-10, 10, -10),
          // q1
          this.getColorRange(-5, 10, -10),
          // q2
          this.getColorRange(0, 10, -10),
          // q3
          this.getColorRange(5, 10, -10),
          // max
          this.getColorRange(10, 10, -10),
        ],
      };
    },
    options() {
      const { rnaType } = this;
      return {
        // stateSave: true,
        stateDuration: -1,
        serverSide: true,
        deferRender: true,
        searchDelay: 1000,
        scrollX: true,
        scrollY: '450px',
        scroller: {
          loadingIndicator: true,
          displayBuffer: 1.5,
        },
        processing: true,
        language: {
          search: this.searchLabel,
        },
        dom: "<'row'<'col-xs-12 col-sm col-md-4 mb-2'E><'col-xs-12 col-sm'f>>" +
          "<'row'<'col-sm-12'tr>>" +
          "<'row'<'col-sm-5'i><'col-sm-7'G>>",
        fixedColumns: true,
        ajax: $.fn.dataTable.pipeline({
          url: this.url,
          method: 'GET',
          pages: 5,
        }),
        columnDefs: [
          {
            searchable: true,
            targets: this.searchableCol,
          },
          {
            searchable: false,
            render(data) {
              return data === 0 ? '-' : data;
            },
            createdCell: (cell, cellData, rowData, rowIdx, colIdx) => {
              if (rnaType === RNA_VALUES[RNA_TYPES.circRNA] && colIdx === 3 && cellData !== '-') {
                return $(cell).html(`<a href="http://www.circbase.org/cgi-bin/singlerecord.cgi?id=${cellData}" target="_blank">
                      ${cellData}
                    </a>`);
              }
              // ignore non meta cell (type of meta cell data is number)
              if (typeof cellData === 'string') {
                return cellData;
              }
              const $cell = $(cell);
              if (cellData === 0) {
                $cell.addClass('heatmap-cell-null');
              } else {
                $cell.addClass('heatmap-cell');
                this.setBgColor($cell, cellData);
              }
              return cellData;
            },
            targets: '_all',
          },
        ],
        selectorOptions: this.selectorOptions,
        legendOptions: this.legendOptions,
      };
    },
  },
  beforeCreate() {
    enablePipeline();
    integratePlugin(HeatmapSelector, 'selectorOptions', 'E');
    integratePlugin(HeatmapLegend, 'legendOptions', 'G');
  },
  mounted() {
    this.bindEvent();
  },
  beforeUpdate() {
    // the tbody of heatmap was generated by DataTables, so we have to remove it manually
    // before the data update
    this.clearTbody();
  },
  activated() {
    if (this.init) {
      this.resizeTable();
    }
  },
  beforeDestroy() {
    this.unbindEvent();
  },
  methods: {
    bindEvent() {
      const $table = $(this.$el);
      $table.on('click', '.heatmap-cell', (e) => {
        this.goToRNA(e.target);
      });
      this.$table = $table;
    },
    unbindEvent() {
      this.$table.off('click', '.heatmap-cell');
    },
    normalizeTumor(tumor) {
      return tumor.split('(')[0];
    },
    goToRNA(cell) {
      // it's wried that we can't get the target row if 'cell' node is passed to api.row()
      // so the jquery object of 'tr' node is passed instead
      const rna = this.dtApi.row($(cell).closest('tr')).data()[0];
      const { metaId } = this.dtApi.column(cell).header().dataset;
      const tumor = this.headers.find(header => header.metaId === metaId).metaName;
      const tumorNormalized = this.normalizeTumor(tumor);
      this.$router.push({
        name: 'rna',
        params: {
          rna,
          rnaType: this.rnaType,
          tumor: tumorNormalized,
        },
      });
    },
    getHsv(val, max = this.legendOptionsScores.max, min = this.legendOptionsScores.min) {
      const range = max - min;
      const middle = (max + min) / 2;
      const halfRange = range / 2;

      const valFixed = Math.max(Math.min(max, val), min);

      const h = Math.floor(((max - valFixed) * 120) / range); // h_max = 120
      const s = Math.abs(valFixed - middle) / halfRange; // s_max = 1
      const v = 1;

      return {
        h,
        s,
        v,
      };
    },
    hsv2rgb({ h, s, v }) { // color range function adapted from http://schinckel.net/2012/01/10/hsv-to-rgb-in-javascript/
      let rgb;
      let i;
      let data = [];
      if (s === 0) {
        rgb = [v, v, v];
      } else {
        let h2 = h;
        h2 /= 60;
        i = Math.floor(h2);
        data = [v * (1 - s), v * (1 - (s * (h2 - i))), v * (1 - (s * (1 - (h2 - i))))];
        switch (i) {
          case 0:
            rgb = [v, data[2], data[0]];
            break;
          case 1:
            rgb = [data[1], v, data[0]];
            break;
          case 2:
            rgb = [data[0], v, data[2]];
            break;
          case 3:
            rgb = [data[0], data[1], v];
            break;
          case 4:
            rgb = [data[2], data[0], v];
            break;
          default:
            rgb = [v, data[0], data[1]];
            break;
        }
      }
      return `#${rgb.map(x => (`0${Math.round(x * 255)
        .toString(16)}`).slice(-2))
        .join('')}`;
    },
    setBgColor($cell, val) {
      const hsv = this.getHsv(val);
      $cell.css('background-color', this.hsv2rgb(hsv));
    },
    getColorRange(val, max, min) {
      const hsv = this.getHsv(val, max, min);
      return this.hsv2rgb(hsv);
    },
    clearTbody() {
      if (this.dtApi) {
        const tbody = this.dtApi.table().body();
        const { parentNode } = tbody;
        // eslint-disable-next-line no-unused-expressions
        parentNode && parentNode.removeChild(tbody);
      }
    },
    resizeTable() {
      const { dtApi } = this;
      // eslint-disable-next-line no-unused-expressions
      dtApi && dtApi.scroller.measure();
    },
    initHandler(...args) {
      this.$emit('init', ...args);
      this.init = true;
      this.dtApi = this.getDTApi();
    },
    stateSaveParamsHandler(...args) {
      this.$emit('stateSaveParams', ...args);
    },
    searchHandler(...args) {
      this.$emit('search', ...args);
    },
    xhrHandler(...args) {
      this.$emit('xhr', ...args);
    },
    getDTApi() {
      return this.$refs.table.getDTApi();
    },
    resetTissueData() {
      this.tissueData = TISSUE_TYPES.tumor;
    },
  },
  watch: {
    dataType() {
      this.resetTissueData();
    },
    rnaType() {
      this.resetTissueData();
    },
  },
};
</script>

<style lang="scss">
  .heatmap-cell {
    cursor: pointer;
    &:hover {
      outline: 1px solid var(--v-primary-base);
      outline-offset: -1px;
      box-shadow: 0 0 8px var(--v-primary-base);
    }
  }

  .heatmap-cell-null {
    background-color: #eee;
    opacity: .65;
    cursor: not-allowed;
  }

  .heatmap-header-flag {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 5px;
  }
  .dataTables_scrollBody {
    .heatmap-header-flag {
      height: 0;
    }
  }

  div.dataTables_wrapper div.dataTables_processing {
    z-index: 1; /* fix 'processing' and 'loading' message overlap */
  }
</style>
